import React from 'react';
import { Link } from 'react-router-dom';
import s from './LegalPage.module.css';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    num: '01',
    title: 'Information We Collect',
    body: (
      <>
        <p>
          Movement Green Pty Ltd ("Movement Green", "we", "us", "our") collects personal
          information when you use our website, submit an enquiry, book an energy assessment,
          or otherwise interact with us. The types of personal information we may collect include:
        </p>
        <ul className={s.list}>
          <li><strong>Contact details</strong> — name, email address, phone number, and residential or business address.</li>
          <li><strong>Property information</strong> — details about your home or premises relevant to identifying VEU-eligible upgrades, such as property type and existing appliances.</li>
          <li><strong>Enquiry and correspondence records</strong> — messages you send us, appointment requests, and service feedback.</li>
          <li><strong>Website usage data</strong> — IP address, browser type, pages visited, and referral source, collected automatically via cookies and analytics tools.</li>
        </ul>
        <p>
          We do not knowingly collect personal information from individuals under the age of 18
          without verifiable parental consent.
        </p>
      </>
    ),
  },
  {
    num: '02',
    title: 'How We Use Your Information',
    body: (
      <>
        <p>We use your personal information to:</p>
        <ul className={s.list}>
          <li>Respond to your enquiries and provide the energy upgrade services you have requested.</li>
          <li>Conduct or arrange a free home energy assessment under the Victorian Energy Upgrades (VEU) program.</li>
          <li>Carry out eligible installations, create Victorian Energy Efficiency Certificates (VEECs), and apply VEU discounts as a VEU Accredited Person (AP).</li>
          <li>Send you updates about VEU rebate changes, new eligible products, and energy-saving tips (where you have opted in).</li>
          <li>Improve our website, services, and communications.</li>
          <li>Comply with our legal obligations under Victorian and Commonwealth law.</li>
        </ul>
        <p>
          We will not use your personal information for any purpose that you would not reasonably
          expect, nor sell it to third-party marketers.
        </p>
      </>
    ),
  },
  {
    num: '03',
    title: 'Sharing Your Information',
    body: (
      <>
        <p>
          Movement Green may share your personal information with trusted third parties in
          the following circumstances:
        </p>
        <ul className={s.list}>
          <li>
            <strong>Installers &amp; the ESC</strong> — We may share your details with
            qualified installers who carry out upgrades under our accreditation, and with the
            Essential Services Commission (ESC) of Victoria as required to create and register
            Victorian Energy Efficiency Certificates (VEECs) and meet our obligations as an
            Accredited Person. These parties are required to handle your information lawfully.
          </li>
          <li>
            <strong>Service providers</strong> — Third-party technology, analytics, and
            communications providers engaged to support our business operations, bound by
            confidentiality obligations.
          </li>
          <li>
            <strong>Legal or regulatory requirements</strong> — Where disclosure is required
            by law, court order, or a government or regulatory authority.
          </li>
        </ul>
        <p>
          We do not share your information with any overseas recipients unless we are
          reasonably satisfied that they uphold privacy standards comparable to the
          Australian Privacy Principles (APPs).
        </p>
      </>
    ),
  },
  {
    num: '04',
    title: 'Cookies & Analytics',
    body: (
      <>
        <p>
          Our website uses cookies and similar tracking technologies to analyse traffic,
          understand how visitors use our site, and improve your experience. Analytics data
          is aggregated and does not personally identify you.
        </p>
        <p>
          You can disable cookies through your browser settings. Doing so may affect certain
          features of the website.
        </p>
      </>
    ),
  },
  {
    num: '05',
    title: 'Data Security',
    body: (
      <>
        <p>
          We take reasonable technical and organisational steps to protect your personal
          information from misuse, interference, loss, unauthorised access, modification, or
          disclosure. These measures include secure storage, access controls, and encrypted
          data transmission (HTTPS).
        </p>
        <p>
          No method of internet transmission is completely secure. While we strive to protect
          your personal information, we cannot guarantee its absolute security.
        </p>
      </>
    ),
  },
  {
    num: '06',
    title: 'Retention',
    body: (
      <p>
        We retain your personal information only for as long as necessary to fulfil the
        purposes for which it was collected, or as required by law. When personal information
        is no longer needed, we destroy or de-identify it securely.
      </p>
    ),
  },
  {
    num: '07',
    title: 'Your Rights',
    body: (
      <>
        <p>
          Under the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles,
          you have the right to:
        </p>
        <ul className={s.list}>
          <li>Request access to the personal information we hold about you.</li>
          <li>Request correction of inaccurate, out-of-date, or incomplete information.</li>
          <li>Withdraw consent to receive marketing communications at any time by unsubscribing or contacting us.</li>
          <li>Lodge a complaint if you believe your privacy rights have been breached.</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us using the details below. We will
          respond to requests within a reasonable timeframe and in accordance with applicable law.
        </p>
      </>
    ),
  },
  {
    num: '08',
    title: 'Complaints',
    body: (
      <p>
        If you believe we have breached the Australian Privacy Principles and wish to make a
        complaint, please contact us in writing. We will acknowledge your complaint within 5
        business days and seek to resolve it within 30 days. If you are not satisfied with our
        response, you may contact the Office of the Australian Information Commissioner (OAIC)
        at <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a>.
      </p>
    ),
  },
  {
    num: '09',
    title: 'Changes to This Policy',
    body: (
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our
        practices or applicable law. The revised policy will be published on this page with
        an updated effective date. We encourage you to review this page periodically.
      </p>
    ),
  },
];

export default function PrivacyPolicyPage() {
  return (
    <main className="page-enter">
      <SEO
        title="Privacy Policy | Movement Green"
        description="Movement Green's Privacy Policy — how we collect, use and protect your personal information in accordance with the Australian Privacy Act 1988."
        noindex
      />

      {/* Hero */}
      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroBadge}>Privacy Policy</div>
          <h1 className={`disp-lg ${s.heroTitle}`}>
            Your Privacy,<br/><em>Our Commitment.</em>
          </h1>
          <p className={s.heroMeta}>Effective date: 30 September 2026 &nbsp;·&nbsp; Movement Green Pty Ltd &nbsp;·&nbsp; Victoria, Australia</p>
        </div>
      </section>

      {/* Body */}
      <section className={s.body}>
        <div className={s.bodyInner}>

          {/* Intro */}
          <div className={s.section}>
            <div className={s.sectionBody}>
              <p>
                Movement Green Pty Ltd ("Movement Green", "we", "us", "our") is committed to
                protecting the privacy of individuals who interact with our website and services.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your
                personal information in accordance with the <em>Privacy Act 1988</em> (Cth) and
                the Australian Privacy Principles (APPs).
              </p>
              <p>
                By using this website or engaging our services, you consent to the practices
                described in this policy. If you do not agree, please discontinue use of our
                website and services.
              </p>
            </div>
          </div>

          <div className={s.divider}/>

          {SECTIONS.map((sec, i) => (
            <React.Fragment key={sec.num}>
              <div className={s.section}>
                <span className={s.sectionNum}>{sec.num}</span>
                <h2 className={s.sectionTitle}>{sec.title}</h2>
                <div className={s.sectionBody}>{sec.body}</div>
              </div>
              {i < SECTIONS.length - 1 && <div className={s.divider}/>}
            </React.Fragment>
          ))}

          <div className={s.divider}/>

          {/* Contact */}
          <div className={s.contact}>
            <div className={s.contactTitle}>Privacy Enquiries</div>
            <p className={s.contactBody}>
              If you have questions about this Privacy Policy or wish to access, correct, or
              complain about the handling of your personal information, please contact us:
            </p>
            <a href="mailto:contact@movementgreen.com.au" className={s.contactEmail}>
              contact@movementgreen.com.au
            </a>
            <p className={s.contactBody} style={{ marginTop: 8 }}>
              Movement Green Pty Ltd &nbsp;·&nbsp; Victoria, Australia
            </p>
          </div>

          <p className="body-sm" style={{ textAlign: 'center' }}>
            Also see our <Link to="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Terms of Use</Link> and <Link to="/disclosure" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Disclosure</Link>.
          </p>

        </div>
      </section>
    </main>
  );
}
